import React, { useContext, useEffect, useState, useCallback } from "react";
import { AuthContext } from "../../context/AuthContext";

// KIRA Diary - Session 3: the logout (evening) check-in.
// A single full-screen, skippable "Closing the day" surface. It is NOT tied
// to an explicit logout click (people just close the tab). Instead it listens
// for the end-of-day signal: clock-out (`diary_end_of_day`), plus a KIRA-driven
// clock-out (`kira_action_complete` / attendance_checkout). It shows at most
// once per day, never blocks, and never nags. Copy + register come from the
// backend (matched to this morning's tone). No em-dashes; emoji backend-governed.

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

const TONES = [
  { key: "good", label: "Good" },
  { key: "steady", label: "Steady" },
  { key: "hard", label: "Hard" },
];

const shownKey = () => `diary_evening_shown_${new Date().toISOString().slice(0, 10)}`;

export default function DiaryLogoutCheckin() {
  const auth = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [tone, setTone] = useState(null);

  const trigger = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token || !auth?.isAuthenticated || auth?.user?.role === "superadmin") return;
    // Once per day only: never nag.
    if (localStorage.getItem(shownKey()) === "1") return;
    localStorage.setItem(shownKey(), "1");
    try {
      const res = await fetch(`${API_BASE}/api/diary/checkin/evening`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return; // non-blocking
      const json = await res.json();
      setTone(json.morning_tone || null); // pre-seat the day's marker to finalise
      setData(json);
      setOpen(true);
    } catch {
      /* non-blocking */
    }
  }, [auth?.isAuthenticated, auth?.user]);

  useEffect(() => {
    const onEnd = () => trigger();
    const onKira = (e) => {
      if (e?.detail?.action_type === "attendance_checkout") trigger();
    };
    window.addEventListener("diary_end_of_day", onEnd);
    window.addEventListener("kira_action_complete", onKira);
    return () => {
      window.removeEventListener("diary_end_of_day", onEnd);
      window.removeEventListener("kira_action_complete", onKira);
    };
  }, [trigger]);

  if (!open || !data) return null;

  const gentle = data.context === "gentle";
  const accent = gentle ? "#8aa6b3" : "#00ff90";
  const close = () => {
    setOpen(false);
    // Let the consent prompt (S5) check for a pending nudge offer.
    try { window.dispatchEvent(new Event("diary_checkin_done")); } catch { /* ignore */ }
  };

  const save = (skipped) => {
    const token = localStorage.getItem("token");
    const transcript = [
      { role: "kira", text: data.opening },
      { role: "kira", text: data.review },
      { role: "kira", text: data.prompt },
      ...(reply.trim() ? [{ role: "user", text: reply.trim() }] : []),
    ];
    try {
      fetch(`${API_BASE}/api/diary/checkin/evening`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          evening_conversation: skipped ? null : transcript,
          felt_evening: skipped ? null : reply.trim() || null,
          tone: skipped ? null : tone,
          skipped,
        }),
      }).catch(() => {});
    } catch {
      /* ignore */
    }
    close();
  };

  const ActionRow = ({ a, done }) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 12px",
        borderRadius: 10,
        border: "1px solid rgba(255,255,255,0.08)",
        background: "#000",
      }}
    >
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: done ? accent : "rgba(255,255,255,0.25)", flexShrink: 0 }} />
      <span style={{ color: done ? "rgba(255,255,255,0.45)" : "#fff", fontSize: 14, textDecoration: done ? "line-through" : "none" }}>
        {a.title}
      </span>
    </div>
  );

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(8,10,12,0.94)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        backdropFilter: "blur(4px)",
      }}
    >
      <button
        onClick={() => save(true)}
        style={{ position: "absolute", top: 20, right: 24, background: "transparent", border: "none", color: "rgba(255,255,255,0.45)", fontSize: 14, cursor: "pointer" }}
      >
        Skip for now
      </button>

      <div style={{ width: "100%", maxWidth: 560, borderRadius: 20, border: `1px solid ${accent}33`, background: "#0d0f11", padding: 32, boxShadow: gentle ? "none" : `0 0 40px ${accent}11`, maxHeight: "90vh", overflowY: "auto" }}>
        <p style={{ fontSize: 20, lineHeight: 1.5, color: "#fff", marginBottom: 14 }}>{data.opening}</p>
        <p style={{ fontSize: 14, lineHeight: 1.5, color: "rgba(255,255,255,0.7)", marginBottom: 18 }}>{data.review}</p>

        {(data.done.length > 0 || data.not_done.length > 0) && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 18 }}>
            {data.done.map((a) => <ActionRow key={a.id} a={a} done />)}
            {data.not_done.map((a) => <ActionRow key={a.id} a={a} done={false} />)}
          </div>
        )}

        <p style={{ fontSize: 15, color: "rgba(255,255,255,0.65)", marginBottom: 12 }}>{data.prompt}</p>
        <textarea
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          rows={3}
          placeholder="In your own words, only if you feel like it."
          style={{ width: "100%", background: "#000", border: `1px solid ${accent}22`, borderRadius: 12, padding: "12px 14px", color: "#fff", fontSize: 14, outline: "none", resize: "vertical", marginBottom: 18 }}
        />
        <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
          {TONES.map((t) => (
            <button
              key={t.key}
              onClick={() => setTone(tone === t.key ? null : t.key)}
              style={{ flex: 1, padding: "10px 0", borderRadius: 10, border: `1px solid ${tone === t.key ? accent : "rgba(255,255,255,0.12)"}`, background: tone === t.key ? `${accent}1f` : "transparent", color: tone === t.key ? accent : "rgba(255,255,255,0.6)", fontSize: 14, cursor: "pointer" }}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button onClick={() => save(true)} style={{ background: "transparent", border: "none", color: "rgba(255,255,255,0.4)", fontSize: 14, cursor: "pointer" }}>
            Skip for now
          </button>
          <button onClick={() => save(false)} style={{ padding: "10px 22px", borderRadius: 10, border: `1px solid ${accent}55`, background: `${accent}1f`, color: accent, fontSize: 14, cursor: "pointer" }}>
            Close the day
          </button>
        </div>
      </div>
    </div>
  );
}
